import React, { useState, useEffect } from "react";
import axios from "axios";
import Constant from "../Constant";
import CreatePlanModal from "./CreatePlanModal";
import EditPlanModal from "./EditPlanModal";
import DeleteConfirmationModal from "../Common/DeleteConfirmationModal";
import Notification from "../Common/Notification";

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [plansPerPage, setPlansPerPage] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [totalPages, setTotalPages] = useState(1);

  // Show notification helper
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Fetch plans on component mount
  useEffect(() => {
    fetchPlans();
  }, [currentPage, plansPerPage]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${Constant.apiBasePath}plans/all`);
      setPlans(response.data);
      setTotalPages(Math.ceil(response.data.length / plansPerPage));
      setLoading(false);
    } catch (error) {
      showNotification("Failed to fetch plans", "error");
      console.error("Error fetching plans:", error);
      setLoading(false);
    }
  };

  // Get current plans for pagination
  const indexOfLastPlan = currentPage * plansPerPage;
  const indexOfFirstPlan = indexOfLastPlan - plansPerPage;
  const currentPlans = plans.slice(indexOfFirstPlan, indexOfLastPlan);

  // Change page
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Handle edit plan
  const handleEditClick = (plan) => {
    setSelectedPlan(plan);
    setShowEditModal(true);
  };

  // Handle delete plan
  const handleDeleteClick = (plan) => {
    setSelectedPlan(plan);
    setShowDeleteModal(true);
  };

  // Delete plan
  const confirmDelete = async () => {
    try {
      await axios.delete(`${Constant.apiBasePath}plans/${selectedPlan._id}`);
      showNotification("Plan deleted successfully");
      setShowDeleteModal(false);
      fetchPlans();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        showNotification(error.response.data.message, "error");
      } else {
        showNotification("Failed to delete plan", "error");
      }
      console.error("Error deleting plan:", error);
    }
  };

  // After create/edit refresh plans
  const onPlanUpdated = () => {
    fetchPlans();
  };

  const handleLimitChange = (e) => {
    setPlansPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing limit
  };

  return (
    <div className="content-page">
      <div className="content">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {notification.show && (
                  <Notification
                    message={notification.message}
                    type={notification.type}
                    onClose={() =>
                      setNotification({ show: false, message: "", type: "" })
                    }
                  />
                )}

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="page-title-heading">Plans Management</h4>
                  {/* <button
                    className="btn btn-info"
                    onClick={() =>
                      showNotification(
                        "This is a test notification!",
                        "success"
                      )
                    }
                  >
                    Test Notification
                  </button> */}
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <span className="me-1">+</span> Create Plan
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden"></span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table dt-responsive nowrap">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Amount</th>
                            <th>Duration (Months)</th>
                            <th>Status</th>
                            <th>Features</th>
                            <th>Description</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentPlans.length > 0 ? (
                            currentPlans.map((plan) => (
                              <tr key={plan._id}>
                                <td>{plan.name}</td>
                                <td>₹{plan.amount}</td>
                                <td>{plan.duration}</td>
                                <td>
                                  <span
                                    className={`badge ${
                                      plan.isActive
                                        ? "bg-success"
                                        : "bg-warning"
                                    }`}
                                  >
                                    {plan.isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td>
                                  <ul className="list-unstyled mb-0">
                                    {plan.planFeatures
                                      .filter(
                                        (feature) => feature.enabled === true
                                      )
                                      .map((feature) => (
                                        <li>{feature.name}</li>
                                      ))}
                                  </ul>
                                </td>
                                <td>{plan.description}</td>
                                <td>
                                  <div className="btn-group">
                                    <button
                                      onClick={() => handleEditClick(plan)}
                                      className="btn btn-primary btn-sm me-2 mr-2"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteClick(plan)}
                                      className="btn btn-danger btn-sm"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center">
                                No plans found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div>
                        <select
                          className="form-select form-select-sm"
                          value={plansPerPage}
                          onChange={handleLimitChange}
                        >
                          <option value="5">5 per page</option>
                          <option value="10">10 per page</option>
                          <option value="25">25 per page</option>
                          <option value="50">50 per page</option>
                        </select>
                      </div>

                      <div>
                        <span className="me-3">
                          Page {currentPage} of {totalPages} (Total:{" "}
                          {plans.length} records)
                        </span>

                        <div className="btn-group">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                          >
                            First
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Prev
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Plan Modal */}
      <CreatePlanModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onPlanCreated={onPlanUpdated}
        showNotification={showNotification}
      />

      {/* Edit Plan Modal */}
      {selectedPlan && (
        <EditPlanModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          plan={selectedPlan}
          onPlanUpdated={onPlanUpdated}
          showNotification={showNotification}
        />
      )}

      {/* Delete Confirmation Modal */}
      {selectedPlan && (
        <DeleteConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Plan"
          message={`Are you sure you want to delete the plan "${selectedPlan.name}"? This action cannot be undone if the plan has no active subscriptions.`}
        />
      )}
    </div>
  );
};

export default Plans;
